import React from 'react';
import * as Sentry from '@sentry/react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

/**
 * ErrorBoundary — catches React render crashes and shows a recovery UI
 * instead of a blank white screen.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[ErrorBoundary] Caught error:', error, errorInfo);
    Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo?.componentStack } } });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-cream flex items-center justify-center p-6">
          <div className="max-w-md w-full text-center">
            <div className="w-10 h-10 rounded-xl bg-red-50 border border-red-200/60 flex items-center justify-center mx-auto mb-5">
              <AlertTriangle className="w-4 h-4 text-red-500" />
            </div>
            <h1 className="text-lg font-display font-semibold text-ink mb-2">
              Something went wrong
            </h1>
            <p className="text-sm font-display text-ink-muted mb-6 leading-relaxed">
              An unexpected error occurred. This has been logged and we'll look into it. 
              Try refreshing the page — if the problem persists, contact support.
            </p>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <pre className="text-left text-xs text-red-600 bg-red-50 border border-red-200/60 rounded-lg p-3 mb-6 max-h-32 overflow-auto font-mono">
                {this.state.error.toString()}
              </pre>
            )}
            <div className="flex items-center justify-center gap-3">
              <button
                onClick={this.handleReload}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-display font-medium bg-accent text-white hover:bg-accent-hover rounded-lg transition-all"
              >
                <RefreshCw className="w-4 h-4" />
                Refresh page
              </button>
              <button
                onClick={this.handleGoHome}
                className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-display font-medium text-ink-muted hover:text-ink hover:bg-cream/60 rounded-lg transition-all"
              >
                Go to dashboard
              </button>
            </div>
            <p className="text-[11px] font-display text-ink-faint mt-6">
              If this keeps happening, email <a href="mailto:support@cayda.io" className="underline hover:text-ink-secondary">support@cayda.io</a>
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
